import React from "react";
import { motion } from "framer-motion";
import {
  Cpu,
  TrendingUp,
  Briefcase,
  Brain,
  HeartHandshake,
  Plane,
  Calculator,
  Users,
  ChevronRight,
} from "lucide-react";
import { UG_PROGRAMS, PG_PROGRAMS, COLLEGE } from "../data/college";
import { useT } from "../i18n/LanguageProvider";

const ICONS = {
  Cpu,
  TrendingUp,
  Briefcase,
  Brain,
  HeartHandshake,
  Plane,
  Calculator,
  Users,
};

const ProgramCard = ({ p, index, t }) => {
  const Icon = ICONS[p.icon] || Cpu;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      className="glass p-7 group relative overflow-hidden"
      data-testid={`ug-program-${p.code}`}
    >
      {/* Top accent bar */}
      <div className="absolute top-0 left-0 h-[2px] w-12 bg-[#00E5FF] group-hover:w-full transition-all duration-500" />

      <div className="flex items-start justify-between mb-5">
        <div className="w-12 h-12 rounded-md flex items-center justify-center bg-[#00E5FF]/10 border border-[#00E5FF]/25 group-hover:bg-[#39FF14]/10 group-hover:border-[#39FF14]/40 transition-colors">
          <Icon className="w-6 h-6 text-[#00E5FF] group-hover:text-[#39FF14] transition-colors" />
        </div>
        <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">
          {p.code}
        </span>
      </div>

      <h3 className="font-heading text-2xl text-white mb-1">{p.title}</h3>
      <p className="font-mono text-xs text-[#94a3b8] mb-5 uppercase tracking-wider">
        {p.sub}
      </p>

      <div className="space-y-2 pt-3 border-t border-white/5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00E5FF] mb-2">
          {t("ug.specializations")}
        </div>
        {p.streams.map((s) => (
          <div
            key={s}
            className="flex items-start gap-2 text-sm text-white/75 leading-tight"
          >
            <ChevronRight className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 flex-shrink-0" />
            <span>{s}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const PGCard = ({ p, index }) => {
  const Icon = ICONS[p.icon] || Plane;
  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="glass p-6 flex items-center gap-5 group"
      data-testid={`pg-program-${p.title}`}
    >
      <div className="w-14 h-14 rounded-md flex items-center justify-center bg-[#39FF14]/8 border border-[#39FF14]/30 flex-shrink-0">
        <Icon className="w-7 h-7 text-[#39FF14]" />
      </div>
      <div className="flex-1">
        <div className="font-heading text-xl text-white">{p.title}</div>
        <div className="text-sm text-[#94a3b8] mt-1">{p.full}</div>
      </div>
    </motion.div>
  );
};

const Programs = () => {
  const { t } = useT();
  return (
    <section
      id="programs"
      className="relative py-16 md:py-24 lg:py-32 border-t border-white/5"
      data-testid="programs-section"
    >
      <div className="absolute inset-0 bg-grid-sm mask-radial opacity-40 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        {/* UG */}
        <div className="mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-12"
          >
            <div className="kicker mb-5">{t("ug.kicker")}</div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              {t("ug.headA")}{" "}
              <span className="text-[#00E5FF] text-glow-cyan">{t("ug.headB")}</span>{" "}
              {t("ug.headC")}
            </h2>
            <p className="text-[#94a3b8] mt-5 text-base md:text-lg max-w-2xl">
              {t("ug.sub")}
            </p>
          </motion.div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="ug-programs-grid"
          >
            {UG_PROGRAMS.map((p, i) => (
              <ProgramCard key={p.code} p={p} index={i} t={t} />
            ))}
          </div>
        </div>

        {/* PG */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10"
          >
            <div className="max-w-2xl">
              <div className="kicker mb-5">{t("pg.kicker")}</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]">
                {t("pg.head")}
              </h2>
            </div>
            <a
              href={`tel:+91${COLLEGE.phone_primary}`}
              className="btn-outline"
              data-testid="programs-call-btn"
            >
              {t("btn.talkAdmissions")}
            </a>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PG_PROGRAMS.map((p, i) => (
              <PGCard key={p.title} p={p} index={i} />
            ))}
          </div>

          <div className="mt-6 text-xs md:text-sm font-mono text-[#FFB300]/90 tracking-wide">
            * Subject to University approval
          </div>
        </div>
      </div>
    </section>
  );
};

export default Programs;
